import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getHolidays, createHoliday, updateHoliday, deleteHoliday, getEventos, crearEvento, editarEvento, borrarEvento,
} from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { detalleError } from '../../components/admin/detalle'
import { rangoFechas } from '../../utils/ausencias'
import CorreoSemanal from '../../components/admin/CorreoSemanal'

// Administración > Calendario: the company's calendar in one place --
// holidays (routers/holidays.py; most come from Buk, which only names them
// "Feriado": rename them here, their date is Buk's) and company events
// (routers/eventos.py: the Christmas celebration, the outing; a "day off for
// everyone" counts like a holiday in everyone's hours). Everyone sees both in
// Personas > Calendario and Plan Week.

const VACIO = { tipo: 'evento', nombre: '', start_date: '', end_date: '', jornada: 'full', libre: false, notas: '' }

export default function AdminCalendario() {
  const { t } = useTranslation(['admin', 'personas', 'common'])
  const [anio, setAnio] = useState(new Date().getFullYear())
  const [feriados, setFeriados] = useState(null)
  const [eventos, setEventos] = useState(null)
  const [form, setForm] = useState(VACIO)
  const [editando, setEditando] = useState(null)     // { tipo, id } | null
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const cargar = () => {
    getHolidays({}).then(setFeriados).catch(() => setFeriados([]))
    getEventos({ desde: `${anio}-01-01`, hasta: `${anio}-12-31` }).then(setEventos).catch(() => setEventos([]))
  }
  useEffect(() => { cargar() }, [anio])   // eslint-disable-line react-hooks/exhaustive-deps

  // One list, by date: holidays and events of the year.
  const filas = useMemo(() => {
    if (!feriados || !eventos) return null
    const hs = feriados.filter(h => h.date.startsWith(String(anio)))
      .map(h => ({ tipo: 'feriado', id: h.id, fecha: h.date, hasta: h.date, nombre: h.name, origen: h.origen }))
    const es = eventos.map(e => ({ tipo: 'evento', id: e.id, fecha: e.start_date, hasta: e.end_date, nombre: e.nombre,
                                   libre: e.libre, mitad: e.mitad, notas: e.notas, evento: e }))
    return [...hs, ...es].sort((a, b) => a.fecha.localeCompare(b.fecha))
  }, [feriados, eventos, anio])

  const esFeriado = form.tipo === 'feriado'
  const unDia = form.start_date && form.start_date === form.end_date
  const set = (cambio) => setForm(f => {
    const g = { ...f, ...cambio }
    if (cambio.start_date && (!g.end_date || g.end_date < g.start_date || g.tipo === 'feriado')) g.end_date = g.start_date
    if (g.start_date !== g.end_date) g.jornada = 'full'
    if (g.jornada !== 'full') g.libre = false
    return g
  })

  const editar = (f) => {
    setError('')
    setEditando({ tipo: f.tipo, id: f.id })
    setForm(f.tipo === 'feriado'
      ? { ...VACIO, tipo: 'feriado', nombre: f.nombre, start_date: f.fecha, end_date: f.fecha, origen: f.origen }
      : { tipo: 'evento', nombre: f.nombre, start_date: f.fecha, end_date: f.hasta, jornada: f.mitad || 'full',
          libre: f.libre, notas: f.notas || '' })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const cancelar = () => { setEditando(null); setForm(VACIO); setError('') }

  const guardar = async (ev) => {
    ev.preventDefault()
    if (!form.nombre.trim() || !form.start_date) { setError(t('calendarioAdmin.faltan')); return }
    setBusy(true); setError('')
    try {
      if (esFeriado) {
        const data = { name: form.nombre.trim(), date: form.start_date }
        if (editando) await updateHoliday(editando.id, form.origen === 'buk' ? { name: data.name } : data)
        else await createHoliday(data)
      } else {
        const data = {
          nombre: form.nombre.trim(), start_date: form.start_date, end_date: form.end_date || form.start_date,
          mitad: unDia && form.jornada !== 'full' ? form.jornada : null,
          libre: form.jornada === 'full' && form.libre, notas: form.notas.trim() || null,
        }
        if (editando) await editarEvento(editando.id, data)
        else await crearEvento(data)
      }
      cancelar()
      cargar()
    } catch (e) {
      setError(detalleError(e, t('calendarioAdmin.failed')))
    }
    setBusy(false)
  }

  const quitar = async (f) => {
    if (!window.confirm(t('calendarioAdmin.borrarConfirm', { nombre: f.nombre }))) return
    setError('')
    try {
      if (f.tipo === 'feriado') await deleteHoliday(f.id)
      else await borrarEvento(f.id)
      if (editando?.id === f.id && editando?.tipo === f.tipo) cancelar()
      cargar()
    } catch (e) {
      setError(detalleError(e, t('calendarioAdmin.failed')))
    }
  }

  const campo = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'
  const etiqueta = 'block text-xs font-medium text-gray-600 mb-1'
  const fechaBuk = esFeriado && editando && form.origen === 'buk'

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <PageHeader title={t('calendarioAdmin.title')} subtitle={t('calendarioAdmin.subtitle')} />
      </div>

      <CorreoSemanal t={t} />

      {/* Add / edit */}
      <form onSubmit={guardar} className="bg-white border border-gray-200 rounded-xl p-5 mb-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {['evento', 'feriado'].map(tp => (
              <button key={tp} type="button" disabled={!!editando} onClick={() => set({ tipo: tp })}
                className={`px-3 py-1 rounded-md text-xs font-medium disabled:opacity-60 ${form.tipo === tp ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {t(`calendarioAdmin.tipo.${tp}`)}
              </button>
            ))}
          </div>
          {editando && <span className="text-xs text-gray-500">{t('calendarioAdmin.editando')}</span>}
        </div>
        <div className={`grid gap-3 ${esFeriado ? 'md:grid-cols-[1fr_auto]' : 'md:grid-cols-[1fr_auto_auto]'}`}>
          <div>
            <label className={etiqueta}>{t('personas:eventos.nombre')}</label>
            <input className={campo} value={form.nombre} onChange={e => set({ nombre: e.target.value })}
              placeholder={esFeriado ? t('calendarioAdmin.feriadoPlaceholder') : t('personas:eventos.nombrePlaceholder')} />
          </div>
          <div>
            <label className={etiqueta}>{esFeriado ? t('calendarioAdmin.fecha') : t('personas:pedir.inicio')}</label>
            <input type="date" className={campo} value={form.start_date} disabled={fechaBuk}
              onChange={e => set({ start_date: e.target.value })} />
          </div>
          {!esFeriado && (
            <div>
              <label className={etiqueta}>{t('personas:pedir.fin')}</label>
              <input type="date" className={campo} value={form.end_date} min={form.start_date || undefined}
                onChange={e => set({ end_date: e.target.value })} />
            </div>
          )}
        </div>
        {fechaBuk && <p className="text-[11px] text-gray-400">{t('calendarioAdmin.fechaBuk')}</p>}
        {!esFeriado && (
          <>
            <div className="flex flex-wrap items-center gap-4">
              {unDia && (
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                  {['full', 'am', 'pm'].map(j => (
                    <button key={j} type="button" onClick={() => set({ jornada: j })}
                      className={`px-3 py-1 rounded-md text-xs font-medium ${form.jornada === j ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                      {t(`personas:pedir.jornada.${j}`)}
                    </button>
                  ))}
                </div>
              )}
              <label className={`flex items-center gap-2 text-sm ${form.jornada !== 'full' ? 'text-gray-300' : 'text-gray-700'}`}>
                <input type="checkbox" className="accent-violet-600" checked={form.libre} disabled={form.jornada !== 'full'}
                  onChange={e => set({ libre: e.target.checked })} />
                {t('personas:eventos.libre')}
              </label>
            </div>
            {form.jornada !== 'full' && <p className="text-[11px] text-gray-400">{t('personas:eventos.medioDiaNoLibre')}</p>}
            <input className={campo} value={form.notas} onChange={e => set({ notas: e.target.value })}
              placeholder={t('personas:eventos.notasPlaceholder')} />
          </>
        )}
        {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div className="flex justify-end gap-2">
          {editando && (
            <button type="button" onClick={cancelar} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
              {t('common:actions.cancel')}
            </button>
          )}
          <button type="submit" disabled={busy}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50">
            {editando ? t('personas:eventos.guardar') : t('calendarioAdmin.agregar')}
          </button>
        </div>
      </form>

      {/* The year */}
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setAnio(a => a - 1)} className="px-2.5 py-1 text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg bg-white">‹</button>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">{anio}</span>
        <button onClick={() => setAnio(a => a + 1)} className="px-2.5 py-1 text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg bg-white">›</button>
      </div>

      {filas === null ? (
        <p className="text-sm text-gray-400">{t('common:actions.loading')}</p>
      ) : filas.length === 0 ? (
        <p className="text-sm text-gray-400 bg-white border border-gray-200 rounded-xl px-5 py-6">{t('calendarioAdmin.vacio', { anio })}</p>
      ) : (
        <ul className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          {filas.map(f => (
            <li key={`${f.tipo}-${f.id}`} className="flex items-center gap-3 px-5 py-2.5">
              <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${f.tipo === 'feriado' ? 'bg-amber-400'
                : f.libre ? 'bg-violet-500' : 'border-2 border-violet-400'}`} />
              <span className="w-40 shrink-0 text-sm text-gray-600 tabular-nums">{rangoFechas(f.fecha, f.hasta)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{f.nombre}</p>
                <p className="text-xs text-gray-500">
                  {f.tipo === 'feriado'
                    ? <>{t('calendarioAdmin.tipo.feriado')}{f.origen === 'buk' && <span className="ml-1.5 text-[10px] border border-gray-200 rounded px-1">Buk</span>}</>
                    : <>
                        {f.libre ? t('personas:eventos.esLibre') : t('personas:eventos.esActividad')}
                        {f.mitad ? ` · ${t(`personas:pedir.jornada.${f.mitad}`)}` : ''}
                        {f.notas ? ` · ${f.notas}` : ''}
                      </>}
                </p>
              </div>
              <button onClick={() => editar(f)} className="text-xs font-medium text-gray-500 hover:text-gray-900">
                {f.tipo === 'feriado' && f.origen === 'buk' ? t('calendarioAdmin.renombrar') : t('personas:eventos.editar')}
              </button>
              {!(f.tipo === 'feriado' && f.origen === 'buk') && (
                <button onClick={() => quitar(f)} className="text-xs font-medium text-gray-400 hover:text-red-600">
                  {t('personas:eventos.borrar')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
